<template>
  <div class="project-mutate-role">
    <Tabs
      v-model="type"
      force-header
      :items="[
        { id: 'project', label: $tc('project') },
        {
          id: 'shop',
          label: $t('shopShort'),
          disabled:
            (!$store.getters.isAdmin && !$store.getters['Project/hasRole']('shop.manager')) ||
            !addons.includes('project:shop'),
        },
        {
          id: 'complaint',
          label: $tc('complaint'),
          disabled: !addons.includes('project:complaint'),
        },
      ]"
      theme="border"
    >
      <template #project>
        <div class="project-mutate-role__input">
          <InputComponent :label="labels.manager" :sublabel="$t('helpers:roleManager')">
            <UserSelect
              :value="getRole('manager')"
              @input="$ev => setRole('manager', $ev)"
              multiple
              search
              :items="$ev => fetchUsers($ev, { type: 'user' })"
              :promise="promise"
              :title="$t('selectx', { x: $tc('projectmanager', 2).toLowerCase() })"
              class="elder-input__element"
            />
          </InputComponent>
          <InputComponent :label="labels.salesman" :sublabel="$t('helpers:roleSalesman')">
            <template #default>
              <UserSelect
                :value="getRole('salesman', { sort: false })"
                @input="$ev => setRole('salesman', $ev)"
                multiple
                search
                :items="fetchUsers"
                :promise="promise"
                sortable
                :title="$t('selectx', { x: $tc('customermanager', 2).toLowerCase() })"
                class="elder-input__element"
              />
            </template>
            <template #after-label>
              <HelperPopover template="salesman-helper" />
            </template>
          </InputComponent>
          <InputComponent
            v-if="
              $store.getters['Project/hasRole']('signingAuthority') &&
              addons.some(addon => ['project:bid', 'project:purchase'].includes(addon))
            "
            :label="labels.signingAuthority"
            :sublabel="$t('helpers:roleSigningAuthority')"
          >
            <template #default>
              <UserSelect
                :value="getRole('signingAuthority')"
                @input="$ev => setRole('signingAuthority', $ev)"
                multiple
                search
                :items="$ev => fetchUsers($ev, { type: 'user' })"
                :promise="promise"
                :title="$t('selectx', { x: $tc('signingAuthority', 2).toLowerCase() })"
                class="elder-input__element"
              />
            </template>
          </InputComponent>
        </div>
      </template>
      <template #shop>
        <Paywall
          :value="addons"
          :items="['project:shop', 'project:shop-purchase']"
          :options="{ units, theme: 'border' }"
          :strategy="addons.includes('project:shop-purchase') && !addons.includes('project:shop') ? 'AND' : 'OR'"
          class="project-mutate-role__input"
          :action="$ev => $store.dispatch('Project/addon', { addon: $ev, state: true })"
        >
          <InputComponent :label="labels.manager" :sublabel="$t('helpers:roleShopManager')">
            <UserSelect
              :value="getRole('shop.manager')"
              @input="$ev => setRole('shop.manager', $ev)"
              multiple
              search
              :items="$ev => fetchUsers($ev, { type: 'user' })"
              :promise="promise"
              :title="$t('selectx', { x: $tc('projectmanager', 2).toLowerCase() })"
              class="elder-input__element"
            />
          </InputComponent>
          <InputComponent :label="labels.salesman" :sublabel="$t('helpers:roleShopSalesman')">
            <template #default>
              <UserSelect
                :value="getRole('shop.salesman', { sort: false })"
                @input="$ev => setRole('shop.salesman', $ev)"
                multiple
                search
                :items="$ev => fetchUsers($ev, { type: 'user' })"
                :promise="promise"
                sortable
                :title="$t('selectx', { x: $tc('customermanager', 2).toLowerCase() })"
                class="elder-input__element"
              />
            </template>
            <template #after-label>
              <HelperPopover template="salesman-shop-helper" />
            </template>
          </InputComponent>

          <InputComponent
            v-if="
              $store.getters['Project/hasRole']('shop.signingAuthority') &&
              addons.some(addon => ['project:shop-purchase'].includes(addon))
            "
            :label="labels.signingAuthority"
            :sublabel="$t('helpers:roleShopSigningAuthority')"
          >
            <template #default>
              <UserSelect
                :value="getRole('shop.signingAuthority')"
                @input="$ev => setRole('shop.signingAuthority', $ev)"
                multiple
                search
                :items="$ev => fetchUsers($ev, { type: 'user' })"
                :promise="promise"
                :title="$t('selectx', { x: $tc('signingAuthority', 2).toLowerCase() })"
                class="elder-input__element"
              />
            </template>
          </InputComponent>
        </Paywall>
      </template>
      <template #complaint>
        <Paywall
          :value="addons"
          :items="['project:complaint']"
          :options="{ units, theme: 'border' }"
          class="project-mutate-role__input"
          :action="$ev => $store.dispatch('Project/addon', { addon: $ev, state: true })"
        >
          <InputComponent :label="labels.complaintmanager" :sublabel="$t('helpers:roleComplaintManager')">
            <UserSelect
              :value="getRole('complaint.manager')"
              @input="$ev => setRole('complaint.manager', $ev)"
              multiple
              search
              :items="$ev => fetchUsers($ev, { type: 'user' })"
              :promise="promise"
              :title="$t('selectx', { x: $tc('projectmanager', 2).toLowerCase() })"
              class="elder-input__element"
            />
          </InputComponent>
        </Paywall>
      </template>
    </Tabs>

    <CustomFields
      v-model="value.customFields"
      :schema="customFieldSchemas"
      :data="data"
      :placement="['roles', 'project:roles']"
      :validator="validator"
      validator-prefix="customFields"
      :disabledFields="$path('metadata.disabledFields.project', item)"
    />
  </div>
</template>

<script>
import UserApi from '@/api/User'
import { mapActions, mapState } from 'vuex'
import { mergeDeepRight, path, assocPath, uniqBy } from 'ramda'
import { Clone, SortByKey } from '@/utils'
import { i18n } from '@/i18n'

import { Paywall } from '@/components/Upsell'
import { AlertComponent as Alert } from 'vue-elder-alert'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent as Checkbox } from 'vue-elder-checkbox'
import UserSelect from '@/components/User/Select/Modal'
import CustomFields from '@kvass/custom-fields'
import Tabs from '@/components/Tabs'
import HelperPopover from '@/components/HelperPopover/Component'
import Bus from '@/bus'

const meta = {
  order: 1005,
  id: 'roles',
  label: i18n.tc('role', 2),
  icon: ['fal', 'user'],
}

export { meta }
export default {
  watch: {
    '$route.query': {
      handler(newValue, oldValue) {
        if (newValue === oldValue) return
        this.type = newValue.tabStep || this.type
      },
      immediate: true,
    },
  },
  props: {
    data: Object,
    value: Object,
    labels: Object,
    validator: Object,
    customFieldSchemas: { type: Array, default: () => [] },
  },
  data() {
    return {
      promise: null,
      type: 'project',
    }
  },
  computed: {
    ...mapState('Project', {
      item(state) {
        return state.item && Object.keys(state.item).length ? state.item : this.data
      },
    }),
    id() {
      return this.$path('item.id')
    },

    addons() {
      return this.$path('item.metadata.addons') || []
    },

    units() {
      return this.$path('item.metadata.limits.units')
    },

    draft() {
      return this.$path('item.draftId') || this.$path('item.metadata.draftAvailable')
    },
  },
  methods: {
    ...mapActions('Project', ['mutate']),
    fetchUsers(innerFilter = {}, outerFilter = {}) {
      return UserApi.get({ filter: { ...innerFilter, ...outerFilter } }, { query: 'UserSearch' })
    },
    getRole(role, options = {}) {
      const { sort = 'name' } = options
      role = role instanceof Array ? role : [role]
      const items = Clone(uniqBy(i => i && i.id, role.map(r => this.$path('item.roles.' + r) || []).flat()))

      if (sort) return items.sort(SortByKey(sort))

      return items
    },
    hasRole(role, value) {
      if (role instanceof Array) return role.some(r => this.hasRole(r, value))
      return Boolean((this.$path('item.roles.' + role) || []).find(r => r.id === value.id))
    },
    setRole(role, value, soft) {
      role = role instanceof Array ? role : [role]

      let getData = cur => {
        let rest = role.filter(r => r !== cur)

        return value.filter(v => !soft || !this.hasRole(rest, v) || this.hasRole(cur, v))
      }

      this.item.roles[role] = getData(role) // Set value in state to prevent sort glitch

      this.promise = this.mutate([
        {
          id: this.id,
          data: {
            roles: role.reduce((res, cur) => {
              return assocPath(
                cur.split('.'),
                getData(cur).map(v => v.id),
                res,
              )
            }, {}),
          },
          getConfig: false,
        },
        {
          transformPayload: false,
          strategy: (item, payload) => {
            return mergeDeepRight(item, {
              draftId: payload.draftId,
              roles: role.reduce((res, cur) => {
                return assocPath(cur.split('.'), getData(cur), res)
              }, {}),
            })
          },
          draft: this.draft,
        },
      ]).then(v => {
        Bus.$emit('draft:update', v.draftId)
      })
    },
  },

  components: {
    InputComponent,
    UserSelect,
    Alert,
    CustomFields,
    Checkbox,
    Tabs,
    HelperPopover,
    Paywall,
  },
}
</script>

<style lang="scss">
.project-mutate-role {
  max-width: 800px;

  &__input {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: 1rem 2rem;
  }

  .elder-input__element {
    max-height: 300px;
    overflow-y: auto;

    @include scrollShadow();
  }

  .select-modal__input {
    .card__content {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .user-card__custom {
      margin-left: auto;
    }
  }

  .tabs {
    &__details {
      border: none !important;
      box-shadow: var(--box-shadow);
    }

    &__header {
      ul,
      a {
        border: none !important;
      }

      a {
        background-color: rgba(black, 0.03);
      }

      &--active a {
        background-color: White;
      }
    }
  }
}
</style>
